/**
 * Typed EventSource wrapper.
 *
 * SSE rather than WebSockets: a chat turn streams in one direction, and `EventSource`
 * brings automatic reconnection and `Last-Event-ID` resume with no code. A WebSocket would
 * mean writing heartbeats and reconnect logic to gain a channel this feature never uses.
 */
import type { ChatEvent } from '@/types/events'

export interface StreamHandlers {
  onEvent: (event: ChatEvent) => void
  onError: (message: string) => void
  onClose: () => void
}

const TERMINAL_EVENTS = new Set(['turn_finished', 'turn_failed'])

export function openTurnStream(url: string, handlers: StreamHandlers): () => void {
  const source = new EventSource(url, { withCredentials: true })
  let closed = false

  const close = () => {
    if (closed) return
    closed = true
    source.close()
    handlers.onClose()
  }

  source.onmessage = (message) => {
    let parsed: ChatEvent
    try {
      parsed = JSON.parse(message.data) as ChatEvent
    } catch {
      // A malformed frame is a backend bug, not something to surface mid-conversation.
      return
    }
    handlers.onEvent(parsed)
    // The server closes after a terminal event; closing here too avoids EventSource's
    // automatic reconnect firing a redundant request for a finished turn.
    if (TERMINAL_EVENTS.has(parsed.type)) close()
  }

  source.onerror = () => {
    if (closed) return
    // EventSource reconnects on a transient drop. Only a fully closed connection is
    // reportable; anything else would flash an error during a normal reconnect.
    if (source.readyState === EventSource.CLOSED) {
      handlers.onError('The connection to the server was lost.')
      close()
    }
  }

  return close
}
